/** 各种卡面共用的小部件：证件照、服务端字段排版、编号与签发日期、“纪念用”声明框。 */
import type { ReactNode } from "react";
import type { CredentialField, CredentialSummary } from "@/shared/contracts";
import { PawBadge, avatarChain, avatarStepKey, speciesArtLabel, speciesArtUrl, useFallbackChain, type AvatarStep } from "@/shared/ui";
import { petPortraitUrl } from "@/features/pets/PetPortrait";
import { sizedPhotoSrc, usePhotoSizes } from "@/shared/ui/photoSizes";
import { faceExtras, fieldLabelEn, isWideField } from "./copy";
import type { WalletPet } from "./data";

type PhotoSource = Pick<WalletPet, "photoUrl" | "photoGenerated" | "idPhoto">;

/** 证件照已经生效：status 为 ready 且给了地址。后端在“有生效照 + 新一轮在画 / 失败”时仍返回 ready 和旧地址，所以证件上不会突然没照片。 */
function readyIdPhotoUrl(pet: Pick<WalletPet, "idPhoto">): string | null {
  const idPhoto = pet.idPhoto;
  return idPhoto && idPhoto.status === "ready" && idPhoto.url ? idPhoto.url : null;
}

/**
 * 证件照（用户 2026-09-24：每一只宠物都要有一张证件照，用在护照、居民证、驾照等所有证件的照片位）——所有照片位只从这里取图。
 * 规则（A 的契约 CharacterState.id_photo，主窗口已核过后端）：id_photo 是 ready 且有 url 时用它（3:4 竖幅浅蓝底）；
 * 否则退回 petPortraitUrl(photo_url)：有照片用照片；演示模式没有照片时是授权的演示小灰猫；live 没有照片时返回 null，照片位显示中性爪印占位。
 * 不用名字首字。
 */
export function credentialPhotoUrl(pet: PhotoSource): string | null {
  return readyIdPhotoUrl(pet) ?? petPortraitUrl(pet.photoUrl);
}

/**
 * 卡包封面的圆头像：有证件照上方裁出的 256×256 头像（id_photo.avatar_url）时用它，没有就照旧 PetPortrait(photo_url)。
 * 后端只在有已生效证件照（status 为 ready）时给 avatar_url；“宠物照片本身当证件照”那种没有裁好的头像，是 null，不拿整图冒充。
 */
export function coverAvatarUrl(pet: Pick<WalletPet, "idPhoto">): string | null {
  const idPhoto = pet.idPhoto;
  return idPhoto && idPhoto.status === "ready" && idPhoto.avatar_url ? idPhoto.avatar_url : null;
}

// 用户 2026-09-29 定：玩家界面不写任何“AI 生成”，原来判“要不要标 AI”的 credentialPhotoIsAi、coverAvatarIsAi 已删；数据字段照旧保留。

/** 证件照片位的一级：已生效的证件照（id），或全站头像退回链里的一级（TA 的照片 / 演示小灰猫 / 物种插画 / 爪印）。 */
export type IdPhotoStep = { kind: "id"; src: string } | AvatarStep;

const idStepKey = (step: IdPhotoStep) => (step.kind === "id" ? `id:${step.src}` : avatarStepKey(step));

/**
 * 证件照片位的退回链（2026-09-27 D4 第二批）：已生效的证件照 → TA 的照片 → 演示小灰猫（只在演示模式）
 * → 物种插画（只给领养来的原创居民，按 WalletPet.origin；规则同 shared/ui 的 portraitFallback）或爪印。
 * 第一级就是 credentialPhotoUrl 原来给的那一张（图不出错时和以前一样）；图打不开才往下退。和证件照同一个地址的照片不算一级。
 */
export function idPhotoChain(pet: Pick<WalletPet, "idPhoto" | "photoUrl" | "species" | "origin">): IdPhotoStep[] {
  const ready = readyIdPhotoUrl(pet);
  const rest = avatarChain(pet.photoUrl, pet.origin, pet.species).filter((step) => !(step.kind === "photo" && step.src === ready));
  return ready ? [{ kind: "id", src: ready }, ...rest] : rest;
}

/**
 * 证件照片位（3:4 的头部在上：图片按顶部对齐裁切，不裁掉头）。按 idPhotoChain 一级一级往下，读屏跟着换：
 * - 证件照一律标“AI 生成”；TA 的照片是服务端生成的形象才标；演示小灰猫不标；
 * - 插画：角上标“插画”（和“AI 生成”同一个小标位置），读屏说“{物种}的插画，不是{名字}本人的照片”——不让人当成证件照；
 * - 爪印（全站同一个徽章，UI-BASE-01b；原来是线条爪印字形）：还没有证件照时照旧说“还没有证件照”；有图但打不开时说“证件照暂时打不开”（不说没有）。
 */
export function IdPhoto({ pet, className }: { pet: WalletPet; className?: string }) {
  const chain = idPhotoChain(pet);
  const { step, at, next } = useFallbackChain(chain, idStepKey);
  // 照片缩略档（UI-PET-PHOTO-SIZES-02）：各张卡上的证件照位大小不一（迷你卡到护照页），统一按 120px 取 384 那档；
  // 只有后端那三种照片地址才带 w（证件照自己的地址不动）；没有这项能力时照旧原图
  const sized = usePhotoSizes();
  const failed = at > 0 && (chain[0].kind === "id" || chain[0].kind === "photo");
  const extra = className ? ` ${className}` : "";
  if (step.kind === "species") {
    return (
      <span className={`ps-idphoto is-species${extra}`} role="img" aria-label={speciesArtLabel(step.species, pet.name)} data-testid="id-photo" data-species={step.species}>
        <img className="ps-species-art" src={speciesArtUrl(step.species)} alt="" draggable={false} />
        <span className="ps-idphoto__tag" aria-hidden="true">
          插画
        </span>
      </span>
    );
  }
  if (step.kind === "paw") {
    return (
      <span className={`ps-idphoto is-empty${extra}`} role="img" aria-label={failed ? `${pet.name}的证件照暂时打不开` : `${pet.name}，还没有证件照`} data-testid="id-photo">
        <PawBadge size={64} className="ps-idphoto__paw" />
      </span>
    );
  }
  const label = step.kind === "demo" && failed ? `${pet.name}的证件照暂时打不开` : `${pet.name}的证件照`;
  return (
    <span className={`ps-idphoto${extra}`} role="img" aria-label={label} data-testid="id-photo">
      <img key={step.src} src={sized ? sizedPhotoSrc(step.src, 120) : step.src} alt="" draggable={false} onError={next} />
    </span>
  );
}

/**
 * 服务端给的卡面字段：标签与内容原样显示，前端只决定排成几栏。
 * bilingual：同一家族证件（护照、星球居民证、驾驶证）在中文标签后加英文小字（只翻译认得的标签）。
 * renderValue：个别证件要控制值里的断行时传入（驾照“成绩”每一科包一个不换行的 span，见 faces.tsx）；字仍是服务端原文，不传就原样。
 */
export function FieldGrid({
  fields,
  layout = "grid",
  bilingual = false,
  renderValue,
}: {
  fields: CredentialField[];
  layout?: "grid" | "stack" | "ticket" | "ruled";
  bilingual?: boolean;
  renderValue?: (field: CredentialField) => ReactNode;
}) {
  if (!fields.length) return null;
  // 票面一行三栏、每栏更窄：超过 8 个字宽（中文算 2）就占两栏，日期、地名不折行。横线行（ruled）一行一个字段，不分栏。
  const wideAt = layout === "ticket" ? 8 : layout === "ruled" ? Number.POSITIVE_INFINITY : 13;
  return (
    <dl className={`ps-credfields ps-credfields--${layout}${bilingual ? " ps-credfields--bilingual" : ""}`}>
      {fields.map((field, index) => {
        const en = bilingual ? fieldLabelEn(field.label) : null;
        return (
          <div key={`${index}-${field.label}`} className={`ps-credfield${layout !== "stack" && isWideField(field, wideAt) ? " is-wide" : ""}`} data-testid="cred-field">
            <dt>
              <span data-testid="field-label">{field.label}</span>
              {en ? <small lang="en">{en}</small> : null}
            </dt>
            <dd data-testid="field-value">{renderValue ? renderValue(field) : field.value}</dd>
          </div>
        );
      })}
    </dl>
  );
}

/** 编号、签发日期：服务端字段里没有写到的才补在卡面底边。 */
export function ExtrasLine({ summary, fields, className, numberLabel = "编号" }: { summary: CredentialSummary; fields: CredentialField[]; className?: string; numberLabel?: string }) {
  const { number, issued } = faceExtras(summary, fields);
  if (!number && !issued) return null;
  return (
    <p className={`ps-face-extras${className ? ` ${className}` : ""}`} data-testid="face-extras">
      {number ? (
        <span>
          <small>{numberLabel}</small> <b className="ps-mono">{number}</b>
        </span>
      ) : null}
      {issued ? (
        <span>
          <small>签发</small> {issued}
        </span>
      ) : null}
    </p>
  );
}

/** 用户参考样式里的虚构声明框：护照、星球居民证、驾驶证左上角都保留。 */
export function FictionBox() {
  return (
    <span className="ps-fiction-box" data-testid="fiction-box">
      纪念用 · 非真实证件
    </span>
  );
}
